import React, { useState, useEffect } from 'react';
import { User, Phone, MapPin, Building, ChevronDown, Contact, Home, Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const countryList = [
     { name: 'India', code: '+91' },
    { name: 'Canada', code: '+1' },
    { name: 'United States', code: '+1' },
    { name: 'United Kingdom', code: '+44' },
    { name: 'Australia', code: '+61' },
    { name: 'Germany', code: '+49' },
    { name: 'France', code: '+33' },
    { name: 'Japan', code: '+81' },
    { name: 'China', code: '+86' },
    { name: 'Brazil', code: '+55' }
];

export default function Address({ isOpen, onClose, mode, addressIndex, onSave, onBackToSaved }) {

    const [formData, setFormData] = useState({
        fullName: '',
        mobileNumber: '',
        email: '',
        streetAddress: '',
        city: '',
        unitNumber: '',
        zipCode: '',
        state: '',
        country: 'India',
        gstin: ''
    });

    useEffect(() => {
        if (!isOpen) return;

        const loadAddressData = async () => {
            const user = auth.currentUser;
            if (mode === 'edit' && addressIndex !== undefined && addressIndex !== null && user) {
                try {
                    let userDocRef = doc(db, "users", user.uid);
                    let docSnap = await getDoc(userDocRef);
                    if (!docSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        docSnap = await getDoc(userDocRef);
                    }
                    if (docSnap.exists()) {
                        const savedAddresses = docSnap.data().addresses || [];
                        if (savedAddresses[addressIndex]) {
                            setFormData(savedAddresses[addressIndex]);
                        }
                    }
                } catch (e) {
                    console.error("Error fetching address details from Firestore", e);
                }
            } else {
                setFormData({
                    fullName: '',
                    mobileNumber: '',
                    email: '',
                    streetAddress: '',
                    city: '',
                    unitNumber: '',
                    zipCode: '',
                    state: '',
                    country: 'India',
                    gstin: ''
                });
            }
        };
        loadAddressData();
    }, [isOpen, mode, addressIndex]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleContinue = async () => {
        // Validate required fields (optional, but good practice)
        if (!formData.fullName || !formData.mobileNumber || !formData.email || !formData.streetAddress || !formData.zipCode || !formData.state) {
            toast.error('Please fill out all required fields marked with *');
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            toast.error('You must be logged in to save addresses.');
            return;
        }

        try {
            let userDocRef = doc(db, "users", user.uid);
            let docSnap = await getDoc(userDocRef);
            if (!docSnap.exists()) {
                userDocRef = doc(db, "users", user.email.toLowerCase());
                docSnap = await getDoc(userDocRef);
            }
            let savedAddresses = [];
            if (docSnap.exists()) {
                savedAddresses = docSnap.data().addresses || [];
            }

            if (mode === 'edit' && addressIndex !== undefined && addressIndex !== null) {
                savedAddresses[addressIndex] = formData;
                toast.success('Address updated successfully!');
            } else {
                savedAddresses.push(formData);
                toast.success('New address saved successfully!');
            }

            await setDoc(userDocRef, { addresses: savedAddresses }, { merge: true });
            if (onSave) onSave();
        } catch (e) {
            console.error("Error saving address to Firestore", e);
            toast.error("Failed to save address. Please try again.");
        }
    };

    if (!isOpen) return null;

    const selectedCountryObj = countryList.find(c => c.name === formData.country) || countryList[0];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 font-sans">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-[#F2F2F2] w-full max-w-4xl max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {/* Header */}
                <div className="bg-[#2c3e2b] text-white p-4 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative">
                    {/* Close button for modal */}
                    <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                        <X size={24} />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-2.5 rounded-full">
                            <User size={24} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Personal Information</h2>
                            <p className="text-sm text-red-50 font-medium">Please provide your contact details</p>
                        </div>
                    </div>
                    {onBackToSaved && (
                        <button onClick={onBackToSaved} className="flex items-center gap-2 bg-white text-[#2c3e2b] px-4 py-2.5 rounded-full font-bold text-sm hover:bg-[#cdcdcd87] hover:text-[#1e1e1e] transition-colors shadow-sm mt-2 sm:mt-0 sm:mr-8 hover:shadow-md hover:-translate-y-0.5 transition-all">
                            <Contact size={18} />
                            Choose from existing addresses
                        </button>
                    )}
                </div>

                {/* Form Body */}
                <div className="p-4 sm:p-6 flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Full Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    placeholder="Enter your full name"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                                <User size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        {/* Mobile Number */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex border border-gray-300 rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-shadow">
                                <div className="bg-[#2c3e2b] text-white flex items-center relative hover:bg-[#ebebeb] hover:text-[#000000] transition-colors group">
                                    <Phone size={16} className="absolute left-3 pointer-events-none" />
                                    <span className="pl-9 pr-6 py-2 font-semibold pointer-events-none">
                                        {selectedCountryObj.code}
                                    </span>
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    >
                                        {countryList.map(c => (
                                            <option key={c.name} value={c.name} className="text-black bg-white">
                                                {c.code} {c.name}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} className="absolute right-2 pointer-events-none" />
                                </div>
                                <input
                                    type="tel"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    placeholder="Enter mobile number"
                                    className="w-full py-2 px-4 outline-none bg-[#fcfcfc] text-black"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Email ID */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Email ID <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter your email ID"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                                <Mail size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        {/* Country */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Country <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 appearance-none outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                >
                                    {countryList.map(c => (
                                        <option key={c.name} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Address Information Header */}
                    <div className="flex items-center gap-2.5 mt-1 mb-1">
                        <div className="bg-[#2c3e2b] text-white p-1.5 rounded-full">
                            <Contact size={18} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Address Information</h3>
                    </div>

                    {/* Street Address */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Street Address<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="streetAddress"
                                value={formData.streetAddress}
                                onChange={handleChange}
                                placeholder="Enter street address"
                                className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                            />
                            <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* City and Unit Number */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">City</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="Enter city"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                                <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Unit Number</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="unitNumber"
                                    value={formData.unitNumber}
                                    onChange={handleChange}
                                    placeholder="Enter unit number"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                                <Home size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Zip Code and State */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Zip Code <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    placeholder="Enter zip code"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                                <MapPin size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                State <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input type="text"
                                    name='state'
                                    value={formData.state}
                                    required
                                    onChange={handleChange}
                                    placeholder="Enter state"
                                    className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 appearance-none outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black"
                                />
                            </div>
                        </div>
                    </div>

                    {/* GSTIN / GST Number */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            GSTIN / GST Number <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="gstin"
                                value={formData.gstin || ''}
                                onChange={handleChange}
                                placeholder="Enter GSTIN (e.g. 32AAAAA0000A1Z5)"
                                className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-10 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-[#fcfcfc] text-black uppercase"
                            />
                            <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-4 p-4 sm:px-8 border-t border-gray-100 bg-gray-50/50">


                    <button
                        onClick={handleContinue}
                        className="btn-primary px-6 py-2.5  hover:-translate-y-0.5 transition-all"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}

